import React, { useState, useEffect } from 'react';
import { Check, X, Info, Save, RefreshCw } from 'lucide-react';
import { useGroupFeatures } from '../hooks/useGroupFeatures.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';

export default function FeatureSelection() {
  const {
    availableFeatures,
    selectedFeatures,
    featureSummary,
    isLoading,
    error,
    updateFeatures,
    isUpdating,
    isFeatureSelected,
    refetch
  } = useGroupFeatures();

  const isAdmin = useIsAdmin();
  const [localSelectedFeatures, setLocalSelectedFeatures] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when data loads
  useEffect(() => {
    if (selectedFeatures.length > 0) {
      setLocalSelectedFeatures([...selectedFeatures]);
      setHasChanges(false);
    }
  }, [selectedFeatures]);

  // Handle feature toggle
  const handleFeatureToggle = (featureName) => {
    setLocalSelectedFeatures(prev => {
      const newSelection = prev.includes(featureName)
        ? prev.filter(f => f !== featureName)
        : [...prev, featureName];
      
      setHasChanges(true);
      return newSelection;
    });
  };

  // Handle save
  const handleSave = () => {
    updateFeatures(localSelectedFeatures);
    setHasChanges(false);
  };

  // Handle reset
  const handleReset = () => {
    setLocalSelectedFeatures([...selectedFeatures]);
    setHasChanges(false);
  };

  // Handle select all
  const handleSelectAll = () => {
    const allFeatureNames = availableFeatures.map(f => f.name);
    setLocalSelectedFeatures(allFeatureNames);
    setHasChanges(true);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setLocalSelectedFeatures([]);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin h-8 w-8 text-green-600" />
        <span className="ml-3 text-gray-600">Loading features...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <X className="h-5 w-5 text-red-400" />
          <span className="ml-2 text-red-800">Error loading features: {error.message}</span>
        </div>
        <button
          onClick={refetch}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <Info className="h-5 w-5 text-yellow-400" />
          <span className="ml-2 text-yellow-800">
            Only group administrators can manage feature selections.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Feature Selection</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Choose which features your group will use. Only features available in your current plan ({featureSummary?.plan_name || 'Unknown Plan'}) can be selected.
        </p>
      </div>

      {/* Summary */}
      {featureSummary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-900">Current Plan: {featureSummary.plan_name}</h3>
              <p className="text-sm text-blue-700 mt-1">
                {featureSummary.total_selected} of {featureSummary.total_available} features selected
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-900">{featureSummary.total_selected}</div>
              <div className="text-xs text-blue-600">Selected</div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleSelectAll}
          className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
        >
          Select All
        </button>
        <button
          onClick={handleDeselectAll}
          className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
        >
          Deselect All
        </button>
        {hasChanges && (
          <button
            onClick={handleReset}
            className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-md hover:bg-yellow-200 transition-colors"
          >
            Reset Changes
          </button>
        )}
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableFeatures
          .filter(feature => feature.name !== 'money_disbursement') // Hide Money Disbursement feature
          .map((feature) => {
          const isSelected = localSelectedFeatures.includes(feature.name);
          
          return (
            <div
              key={feature.name}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                isSelected
                  ? 'border-green-300 bg-green-50 shadow-sm'
                  : 'border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:shadow-sm'
              }`}
              onClick={() => handleFeatureToggle(feature.name)}
            >
              {/* Selection Indicator */}
              <div className="absolute top-3 right-3">
                {isSelected ? (
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full" />
                )}
              </div>

              {/* Feature Content */}
              <div className="pr-8">
                <h3 className="font-medium text-gray-900 mb-1">
                  {feature.label}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </div>

              {/* Feature Name (for debugging) */}
              <div className="mt-2 text-xs text-gray-400 font-mono">
                {feature.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={isUpdating}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isUpdating ? (
              <RefreshCw className="animate-spin h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="space-y-1">
              <li>• Only features available in your current plan can be selected</li>
              <li>• Selected features will be shown in your dashboard and navigation</li>
              <li>• You can change your selections at any time</li>
              <li>• Changes take effect immediately for all group members</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 